import { useCallback, useContext } from "react";
import { UserContext } from "../contexts/UserContext";
/**
 * Hook do logowania zdarzeń
 *
 * Zapewnia integrację systemu logowania z React Context API.
 * Automatycznie pobiera dane użytkownika z kontekstu i udostępnia
 * funkcje logowania dla komponentów i innych hooków.
 *
 * Funkcjonalności:
 * - Integracja z UserContext
 * - Wrapper dla serwisu logowania
 * - Optymalizacja przez useCallback
 * - Obsługa przypadku braku użytkownika
 */

import {
  logUserAction,
  logEventWithFullUser,
  logError,
  logInfo,
  logSuccess,
} from "../services/loggingService";
import {
  EventType,
  LogAction,
  EntityType,
  LogDataWithFullUser,
  LoggingResult,
  LoggingConfig,
  DEFAULT_LOGGING_CONFIG,
} from "../interfaces/ILogs";

/**
 * Interfejs zwracany przez hook useLogging
 */
export interface UseLoggingReturn {
  /** Funkcja logowania z kontekstem użytkownika */
  logAction: (
    eventType: EventType,
    action: LogAction,
    entityType: EntityType,
    entityId: string,
    beforeState: any,
    afterState: any,
    success: boolean,
    errorMessage?: string,
    metadata?: any,
    config?: LoggingConfig,
    providedUser?: any
  ) => Promise<LoggingResult>;

  /** Funkcja logowania z pełnymi danymi użytkownika */
  logEventWithFullUser: (
    logData: LogDataWithFullUser,
    config?: LoggingConfig
  ) => Promise<LoggingResult>;

  /** Funkcja logowania błędów */
  logError: (error: Error, context: string) => Promise<void>;

  /** Funkcja logowania informacji */
  logInfo: (message: string, data?: any) => void;

  /** Funkcja logowania sukcesu */
  logSuccess: (message: string, data?: any) => void;

  /** Czy użytkownik jest zalogowany (czy logowanie jest dostępne) */
  isLoggingAvailable: boolean;

  /** Dane użytkownika (jeśli dostępne) */
  user: any | null;
}

/**
 * Hook do logowania zdarzeń
 *
 * @returns UseLoggingReturn - Funkcje i dane do logowania
 */
export const useLogging = (): UseLoggingReturn => {
  // Bezpieczne pobieranie kontekstu użytkownika - może być null gdy użytkownik nie jest zalogowany
  const context = useContext(UserContext);
  const user = context?.user || null;

  /**
   * Funkcja logowania z kontekstem użytkownika
   *
   * @param eventType - Typ zdarzenia
   * @param action - Typ akcji
   * @param entityType - Typ encji
   * @param entityId - ID encji
   * @param beforeState - Stan przed operacją
   * @param afterState - Stan po operacji
   * @param success - Czy operacja się powiodła
   * @param errorMessage - Komunikat błędu (opcjonalny)
   * @param metadata - Dodatkowe metadane (opcjonalne)
   * @param config - Konfiguracja logowania (opcjonalna)
   * @param providedUser - Opcjonalny użytkownik przekazany jako parametr (dla komponentów logowania)
   * @returns Promise<LoggingResult> - Wynik operacji logowania
   */
  const logAction = useCallback(
    async (
      eventType: EventType,
      action: LogAction,
      entityType: EntityType,
      entityId: string,
      beforeState: any,
      afterState: any,
      success: boolean,
      errorMessage?: string,
      metadata?: any,
      config: LoggingConfig = DEFAULT_LOGGING_CONFIG,
      providedUser?: any
    ): Promise<LoggingResult> => {
      // Używamy przekazanego użytkownika lub użytkownika z kontekstu
      const currentUser = providedUser || user;

      // Sprawdzenie czy użytkownik jest dostępny
      if (!currentUser) {
        const error = "Użytkownik nie jest dostępny - logowanie niemożliwe";
        // Nie logujemy ostrzeżenia - to jest normalne dla komponentów logowania
        if (config.throwOnError) {
          throw new Error(error);
        }

        return { success: false, error };
      }

      try {
        const result = await logUserAction(
          eventType,
          action,
          entityType,
          entityId,
          beforeState,
          afterState,
          currentUser,
          success,
          errorMessage,
          metadata,
          config
        );

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Nieznany błąd podczas logowania";
        console.error("❌ [useLogging] Wyjątek podczas logowania:", {
          error: errorMessage,
          eventType,
          entityId,
        });

        if (config.throwOnError) {
          throw error;
        }

        return { success: false, error: errorMessage };
      }
    },
    [user]
  );

  /**
   * Funkcja logowania z pełnymi danymi użytkownika
   *
   * @param logData - Dane logu z pełnymi danymi użytkownika
   * @param config - Konfiguracja logowania (opcjonalna)
   * @returns Promise<LoggingResult> - Wynik operacji logowania
   */
  const logEventWithFullUser = useCallback(
    async (
      logData: LogDataWithFullUser,
      config: LoggingConfig = DEFAULT_LOGGING_CONFIG
    ): Promise<LoggingResult> => {
      try {
        const result = await logEventWithFullUser(logData, config);



        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Nieznany błąd podczas logowania";
        console.error("❌ [useLogging] Wyjątek podczas logowania z pełnymi danymi:", {
          error: errorMessage,
          eventType: logData.eventType,
        });

        if (config.throwOnError) {
          throw error;
        }

        return { success: false, error: errorMessage };
      }
    },
    []
  );

  /**
   * Funkcja logowania błędów
   *
   * @param error - Błąd do zalogowania
   * @param context - Kontekst błędu
   */
  const logError = useCallback(async (error: Error, context: string): Promise<void> => {
    await logError(error, context);
  }, []);

  /**
   * Funkcja logowania informacji
   *
   * @param message - Wiadomość do zalogowania
   * @param data - Dodatkowe dane (opcjonalne)
   */
  const logInfo = useCallback((message: string, data?: any): void => {
    logInfo(message, data);
  }, []);

  /**
   * Funkcja logowania sukcesu
   *
   * @param message - Wiadomość do zalogowania
   * @param data - Dodatkowe dane (opcjonalne)
   */
  const logSuccess = useCallback((message: string, data?: any): void => {
    logSuccess(message, data);
  }, []);

  return {
    logAction,
    logEventWithFullUser,
    logError,
    logInfo,
    logSuccess,
    isLoggingAvailable: !!user,
    user,
  };
};

/**
 * Hook do logowania konkretnych typów zdarzeń
 *
 * @returns Obiekt z funkcjami do logowania konkretnych zdarzeń
 */
export const useLoggingActions = () => {
  const { logAction } = useLogging();

  /**
   * Logowanie rejestracji użytkownika
   */
  const logUserRegistration = useCallback(
    async (user: any, success: boolean, errorMessage?: string) => {
      return logAction(
        EventType.USER_REGISTRATION,
        LogAction.REGISTER,
        EntityType.USER,
        user.id,
        null,
        user,
        success,
        errorMessage,
        undefined,
        DEFAULT_LOGGING_CONFIG,
        user
      );
    },
    [logAction]
  );

  /**
   * Logowanie logowania użytkownika
   */
  const logUserLogin = useCallback(
    async (user: any, success: boolean, errorMessage?: string) => {
      return logAction(
        EventType.USER_LOGIN,
        LogAction.LOGIN,
        EntityType.USER,
        user.id,
        null,
        user,
        success,
        errorMessage,
        undefined,
        DEFAULT_LOGGING_CONFIG,
        user
      );
    },
    [logAction]
  );

  /**
   * Logowanie dodania zawodnika
   */
  const logParticipantCreate = useCallback(
    async (participant: any, success: boolean, errorMessage?: string, metadata?: any) => {
      return logAction(
        EventType.PARTICIPANT_CREATE,
        LogAction.CREATE,
        EntityType.PARTICIPANT,
        participant.id,
        null,
        participant,
        success,
        errorMessage,
        metadata
      );
    },
    [logAction]
  );

  /**
   * Logowanie edycji zawodnika
   */
  const logParticipantUpdate = useCallback(
    async (
      oldParticipant: any,
      newParticipant: any,
      success: boolean,
      errorMessage?: string,
      metadata?: any
    ) => {
      return logAction(
        EventType.PARTICIPANT_UPDATE,
        LogAction.UPDATE,
        EntityType.PARTICIPANT,
        newParticipant.id,
        oldParticipant,
        newParticipant,
        success,
        errorMessage,
        metadata
      );
    },
    [logAction]
  );

  /**
   * Logowanie usunięcia zawodnika
   */
  const logParticipantDelete = useCallback(
    async (
      participant: any,
      success: boolean,
      errorMessage?: string,
      metadata?: any,
      afterState?: any
    ) => {
      return logAction(
        EventType.PARTICIPANT_DELETE,
        LogAction.DELETE,
        EntityType.PARTICIPANT,
        participant.id,
        participant,
        afterState || null,
        success,
        errorMessage,
        metadata
      );
    },
    [logAction]
  );

  /**
   * Logowanie utworzenia zgłoszenia
   */
  const logApplicationCreate = useCallback(
    async (application: any, success: boolean, errorMessage?: string, metadata?: any) => {
      return logAction(
        EventType.APPLICATION_CREATE,
        LogAction.CREATE,
        EntityType.APPLICATION,
        application.id,
        null,
        application,
        success,
        errorMessage,
        metadata
      );
    },
    [logAction]
  );

  /**
   * Logowanie edycji zgłoszenia
   */
  const logApplicationUpdate = useCallback(
    async (
      oldApplication: any,
      newApplication: any,
      success: boolean,
      errorMessage?: string,
      metadata?: any
    ) => {
      return logAction(
        EventType.APPLICATION_UPDATE,
        LogAction.UPDATE,
        EntityType.APPLICATION,
        newApplication.id,
        oldApplication,
        newApplication,
        success,
        errorMessage,
        metadata
      );
    },
    [logAction]
  );

  /**
   * Logowanie usunięcia zgłoszenia
   */
  const logApplicationDelete = useCallback(
    async (application: any, success: boolean, errorMessage?: string, metadata?: any) => {
      return logAction(
        EventType.APPLICATION_DELETE,
        LogAction.DELETE,
        EntityType.APPLICATION,
        application.id,
        application,
        null,
        success,
        errorMessage,
        metadata
      );
    },
    [logAction]
  );

  /**
   * Logowanie zmiany statusu zgłoszenia
   */
  const logApplicationStatusChange = useCallback(
    async (
      application: any,
      oldStatus: string,
      newStatus: string,
      success: boolean,
      errorMessage?: string,
      metadata?: any
    ) => {
      return logAction(
        EventType.APPLICATION_STATUS_CHANGE,
        LogAction.UPDATE,
        EntityType.APPLICATION,
        application.id,
        { status: oldStatus },
        { status: newStatus },
        success,
        errorMessage,
        { ...metadata, oldStatus, newStatus }
      );
    },
    [logAction]
  );

  /**
   * Logowanie dodania prezentacji
   */
  const logPresentationCreate = useCallback(
    async (presentation: any, success: boolean, errorMessage?: string, metadata?: any) => {
      return logAction(
        EventType.PRESENTATION_CREATE,
        LogAction.CREATE,
        EntityType.PRESENTATION,
        presentation.id,
        null,
        presentation,
        success,
        errorMessage,
        metadata
      );
    },
    [logAction]
  );

  /**
   * Logowanie edycji prezentacji
   */
  const logPresentationUpdate = useCallback(
    async (
      oldPresentation: any,
      newPresentation: any,
      success: boolean,
      errorMessage?: string,
      metadata?: any
    ) => {
      return logAction(
        EventType.PRESENTATION_UPDATE,
        LogAction.UPDATE,
        EntityType.PRESENTATION,
        newPresentation.id,
        oldPresentation,
        newPresentation,
        success,
        errorMessage,
        metadata
      );
    },
    [logAction]
  );

  /**
   * Logowanie usunięcia prezentacji
   */
  const logPresentationDelete = useCallback(
    async (presentation: any, success: boolean, errorMessage?: string, metadata?: any) => {
      return logAction(
        EventType.PRESENTATION_DELETE,
        LogAction.DELETE,
        EntityType.PRESENTATION,
        presentation.id,
        presentation,
        null,
        success,
        errorMessage,
        metadata
      );
    },
    [logAction]
  );

  return {
    // Uwierzytelnianie
    logUserRegistration,
    logUserLogin,

    // Zawodnicy
    logParticipantCreate,
    logParticipantUpdate,
    logParticipantDelete,

    // Zgłoszenia
    logApplicationCreate,
    logApplicationUpdate,
    logApplicationDelete,
    logApplicationStatusChange,

    // Prezentacje
    logPresentationCreate,
    logPresentationUpdate,
    logPresentationDelete,
  };
};
